import { useCallback, useMemo, useState } from 'react';

// Password policy (8~64 + letter+digit+symbol) is the canonical rule from PRD 21 §5.2.
// Re-exported so existing SignupForm imports keep working while the logic lives in one place.
import { isValidPassword } from '@/shared/utils/password';
import { PHONE_REGEX, toPhoneInputValue } from '@/shared/utils/phone';
export { isValidPassword };

// PRD 19 §4.1/§4.2 — validation rules. The phone rule itself is not local: PRD 19 §4.1 points at
// `[공통] 00 §2.1`, so `PHONE_REGEX` (^010\d{8}$) is imported rather than restated here.
//
// It used to be restated, as `/^01\d-\d{3,4}-\d{4}$/`, back when this screen kept the value
// hyphenated. SHCT-695 made `setField` store digits only — but left that regex behind, and a
// digits-only value can never match a pattern whose hyphens are mandatory. `isValidPhone` returned
// false for every input, which disabled [인증번호 받기] and the submit with it: the screen could not
// be completed at all. Sharing the constant is what stops the two halves drifting apart again.
// PRD 19 §4.1 (row) / PRD 21 §5.4.3 canonical — name length is 2~50 chars.
const NAME_MIN = 2;
const NAME_MAX = 50;

export interface SignupFormValues {
  password: string;
  passwordConfirm: string;
  name: string;
  phone: string;
  code: string;
}

export type SignupFieldName = keyof SignupFormValues;

export type SignupFormErrors = Partial<Record<SignupFieldName, string>>;

export interface UseSignupFormOptions {
  initialName?: string;
}

const INITIAL_VALUES: SignupFormValues = {
  password: '',
  passwordConfirm: '',
  name: '',
  phone: '',
  code: '',
};

export function isValidName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= NAME_MIN && trimmed.length <= NAME_MAX;
}

export function isValidPhone(phone: string): boolean {
  return PHONE_REGEX.test(phone);
}

// Digits-only filter for the SMS code input.
export function formatCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

export interface UseSignupFormReturn {
  values: SignupFormValues;
  errors: SignupFormErrors;
  setField: (field: SignupFieldName, value: string) => void;
  setError: (field: SignupFieldName, message: string | undefined) => void;
  clearErrors: () => void;
  /**
   * Marks the phone as having passed SMS verification. Set true after a 204 from
   * verify-otp — the BE marks the phone verified server-side (keyed by phone), so the
   * FE only holds a local boolean, no opaque handle.
   */
  phoneVerified: boolean;
  setPhoneVerified: (value: boolean) => void;
  /** Derived — Receive-code button enables once phone format is valid and not already verified. */
  canRequestCode: boolean;
  /** Derived — Submit-enabled per PRD 19 §3.3 step 5. */
  submitEnabled: boolean;
}

export function useSignupForm(options: UseSignupFormOptions = {}): UseSignupFormReturn {
  const [values, setValues] = useState<SignupFormValues>({
    ...INITIAL_VALUES,
    name: options.initialName?.trim() ?? '',
  });
  const [errors, setErrors] = useState<SignupFormErrors>({});
  const [phoneVerified, setPhoneVerifiedState] = useState(false);

  const setField = useCallback<UseSignupFormReturn['setField']>((field, value) => {
    setValues((prev) => {
      const normalized =
        // SHCT-695 — the phone field is normalized here, not just at the call
        // site. It used to run a `formatPhone` that re-inserted the hyphens the
        // component had just stripped, so the ticket's rule never actually
        // reached the signup screen. `[공통] 00 §2.1` governs 입력, so the value
        // stored and displayed is digits-only.
        field === 'phone' ? toPhoneInputValue(value)
          : field === 'code' ? formatCode(value)
            : value;
      // PRD 19 §3.3 — changing phone after verification invalidates the verification.
      // Submit will fall back to disabled.
      if (field === 'phone' && prev.phone !== normalized) {
        setPhoneVerifiedState(false);
      }
      return { ...prev, [field]: normalized };
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const setError = useCallback<UseSignupFormReturn['setError']>((field, message) => {
    setErrors((prev) => {
      if (!message) {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: message };
    });
  }, []);

  const clearErrors = useCallback(() => setErrors({}), []);

  const canRequestCode = useMemo(
    () => isValidPhone(values.phone) && !phoneVerified,
    [values.phone, phoneVerified],
  );

  const submitEnabled = useMemo(() => {
    return (
      isValidPassword(values.password) &&
      values.passwordConfirm.length > 0 &&
      values.password === values.passwordConfirm &&
      isValidName(values.name) &&
      isValidPhone(values.phone) &&
      phoneVerified
    );
  }, [values, phoneVerified]);

  return {
    values,
    errors,
    setField,
    setError,
    clearErrors,
    phoneVerified,
    setPhoneVerified: setPhoneVerifiedState,
    canRequestCode,
    submitEnabled,
  };
}
